import React from 'react';
import EmptyStateIcon, {
  EmptyStateWrapper,
  NO_DATA_FOR_PERIOD_MESSAGE,
} from 'components/_common/EmptyState';
import { Box } from '@mui/material';
import { notUndefiend, valueAsString } from 'helpers/conversions';
import { StatsData } from '../Card';
import { dashboardIcon } from '../Icons';
import { Spinner } from '../Styles';
import { ReactComponent as ArrowUpIcon } from '../../../assets/icons/TopCardsArrowUp.svg';
import { ReactComponent as ArrowDownIcon } from '../../../assets/icons/TopCardsArrowDown.svg';
import { TimeFrameEnum } from 'models/ReportData';
import {
  OverviewTopCardNames,
  getDescriptionTextForCustomTimeframe,
  getTopCardArrowColor,
} from 'helpers/overviewUtils';
import { CardProps } from './DesktopTopCards.types';
import { dtgStyles as styles } from './DesktopAvarageProductionRateCard.styles';
import { animations } from 'theme';
import { styles as topCardStyles } from './DesktopTotalImpressionCard.styles';

type AverageProductionRateCardProps = Omit<CardProps, 'addedData'> & {
  addedData?: CardProps['addedData'];
  isProductionMode?: boolean;
};

const DesktopAverageProductionRateCardDtg = (props: AverageProductionRateCardProps) => {
  const {
    stats,
    description,
    value,
    units,
    statsDescription,
    addedData,
    timeframe,
    customTimeframeProperties,
    isProductionMode,
  } = props;
  const isNegative = React.useMemo(() => {
    if (!notUndefiend([stats])) {
      return false;
    }
    if (stats! < 0) {
      return true;
    }
    if (description === 'Average Availability' && value !== undefined && value >= 0.999) {
      return false;
    }
    return false;
  }, [description, stats, value]);

  const getArrowIcon = (data, row) => {
    const trend = data[row].trend;
    const description = data[row].description;
    if (trend === 0 || trend === undefined) {
      return <></>;
    }
    return trend > 0 ? (
      <ArrowUpIcon
        fill={getTopCardArrowColor({
          cardName: OverviewTopCardNames.averageProductionRate,
          trend,
          description,
        })}
      />
    ) : (
      <ArrowDownIcon
        fill={getTopCardArrowColor({
          cardName: OverviewTopCardNames.averageProductionRate,
          trend,
          description,
        })}
      />
    );
  };
  const hasAddedData = !!addedData;
  if (value === null) {
    return (
      <EmptyStateWrapper>
        <EmptyStateIcon icon="no-icon" message={NO_DATA_FOR_PERIOD_MESSAGE} />
      </EmptyStateWrapper>
    );
  }

  return notUndefiend([value]) ? (
    <Box
      display="flex"
      justifyContent="space-between"
      width="100%"
      height="100%"
      boxSizing="border-box"
      sx={{
        //*vertical padding in px - see the note in DesktopTotalImpressionCard.styles.ts (a % padding is width-relative and breaks row alignment)
        //*Production Hours mode: the card is ~16% wide instead of 25%, so a larger % keeps the side padding in line with the neighbouring cards
        padding: isProductionMode ? '12px 7%' : '12px 4%',
        borderRadius: 'inherit',
        ...animations.appearanceEffect,
        '@media (min-width: 2250px)': {
          padding: isProductionMode ? '12px 1.7%' : '12px 1%',
        },
      }}
      data-testid="average-production-rate-card">
      <Box
        display="flex"
        boxSizing="border-box"
        height="100%"
        width={hasAddedData ? '50%' : '100%'}
        sx={styles.leftColumn}
        data-testid="left-column">
        <Box
          display="flex"
          flexDirection="column"
          boxSizing="border-box"
          width="100%"
          height="100%"
          sx={styles.leftColumnCardContent}
          data-testid="left-column-content">
          <Box
            display="flex"
            flexDirection="column"
            boxSizing="border-box"
            width="100%"
            height="80px"
            sx={styles.upperRow}
            data-testid="upper-row">
            <Box display="flex" alignItems="flex-start" data-testid="upper-row-content">
              {isNegative ? dashboardIcon.negative : dashboardIcon.positive}{' '}
              <Box sx={topCardStyles.value} data-testid="avg-prod-rate-value">
                {valueAsString(value, false)}
              </Box>
              <Box sx={topCardStyles.units} data-testid="avg-prod-rate-units">
                {units}
              </Box>
            </Box>
            <Box
              sx={[topCardStyles.description, { paddingTop: '2px' }]}
              data-testid="avg-prod-rate-description">
              {description}
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="flex-end"
            width="100%"
            height="100%"
            boxSizing="border-box"
            sx={topCardStyles.bottomRow}
            data-testid="bottom-row">
            {notUndefiend([stats]) && StatsData(stats!)}{' '}
            <div>
              {timeframe === TimeFrameEnum.custom
                ? getDescriptionTextForCustomTimeframe({ timeframe, customTimeframeProperties })
                : statsDescription}
            </div>
          </Box>
        </Box>
      </Box>

      {hasAddedData && (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          boxSizing="border-box"
          height="100%"
          width="40%"
          sx={styles.rightColumn}
          data-testid="right-column">
          <Box
            display="flex"
            alignItems="center"
            boxSizing="border-box"
            width="100%"
            height="50px"
            sx={styles.rightColumnUpperRow}
            data-testid="right-column-upper-row">
            <Box
              display="flex"
              width="20px"
              alignSelf="center"
              sx={styles.rightColumnArrowPlaceHolder}
              data-testid="upper-arrow-placeholder">
              {getArrowIcon(addedData, 'upper')}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              sx={styles.rightColumnContent}
              data-testid="upper-content">
              <Box
                display="flex"
                sx={styles.rightColumnContentUpperRow}
                data-testid="upper-content-value">
                {valueAsString(addedData?.upper?.value)} {addedData?.upper?.unit}
              </Box>
              <Box sx={styles.rightColumnContentBottomRow} data-testid="upper-content-description">
                {addedData?.upper?.description}
              </Box>
            </Box>
          </Box>
          {addedData?.lower && (
            <Box
              display="flex"
              boxSizing="border-box"
              width="100%"
              sx={styles.rightColumnBottomRow}
              data-testid="right-column-bottom-row">
              <Box
                display="flex"
                width="20px"
                alignSelf="center"
                sx={styles.rightColumnArrowPlaceHolder}
                data-testid="lower-arrow-placeholder">
                {getArrowIcon(addedData, 'lower')}
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                sx={styles.rightColumnContent}
                data-testid="lower-content">
                <Box
                  display="flex"
                  sx={styles.rightColumnContentUpperRow}
                  data-testid="lower-content-value">
                  {valueAsString(addedData?.lower?.value * 100)} {addedData?.lower?.unit}
                </Box>
                <Box
                  sx={styles.rightColumnContentBottomRow}
                  data-testid="lower-content-description">
                  {addedData?.lower?.description}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : (
    Spinner(60)
  );
};
export default DesktopAverageProductionRateCardDtg;
